import React, { useCallback, useState } from 'react';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import axios from 'axios';
import { isNil } from 'lodash';

import { useStyles } from '~/assets/styles';
import type { LineItemColumnId } from '~/components/AiChat/SideBarDialog/InvoiceRecommendationAnalysis/columnUtils';
import { ConfiguredInvoiceRecommendationAnalysis } from '~/components/AiChat/SideBarDialog/InvoiceRecommendationAnalysis/ConfiguredInvoiceRecommendationAnalysis';
import type {
  InvoiceAnalysisResponse,
  LineItemProps,
  LineItemRow,
} from '~/components/AiChat/SideBarDialog/InvoiceRecommendationAnalysis/types';
import { isDepreciationEligible } from '~/components/AiChat/SideBarDialog/InvoiceRecommendationAnalysis/utils';
import { useClaim } from '~/components/ClaimContainer';
import Button from '~/components/core/Atomic/Buttons/Button';
import Menu from '~/components/core/Atomic/Menu';
import MenuItem from '~/components/core/Atomic/MenuItem';
import useCurrencyFormatter from '~/components/CurrencyFormatterContext';
import { AddIcon } from '~/components/deprecatedMuiIcons';
import AddInvoiceForExposureDialog from '~/components/exposures/AddInvoiceForExposureDialog';
import { wasDeductibleApplied } from '~/components/exposures/GcDeductibleContainer';
import PaymentRequestContainer from '~/components/exposures/PaymentRequestContainer/PaymentRequestContainer';
import { useDamageAssessmentConfig } from '~/components/hooks/useDamageAssessmentConfig';
import { useInvoices } from '~/components/invoices/hooks/useInvoices';
import type { ExposureModel } from '~/components/types/exposure-types';
import type { LineItemWithInvoiceInfo } from '~/components/types/invoice-types';
import FINANCIAL_STATUS_DICT from '~/server_shared/generated-types/FINANCIAL_STATUS_DICT';
import type {
  UpdateExposureLineItemsRequestCoverageDecisionOption,
  UpdateExposureLineItemsRequestPayableType,
} from '~/server_shared/openapi/data-contracts';
import { reportAxiosError, reportErrorInProductionOrThrow } from '~/Utils';
import { CURRENCY_OPTIONS, getPolicyCurrency } from '~/Utils/currencyUtils';
import { DECIMAL_ZERO } from '~/Utils/decimalUtils';
import { multiplyToCentsDecimal, toCentsDecimal } from '~/Utils/toCents';

interface Props {
  exposure: ExposureModel;
  open: boolean;
  onClose: () => void;
}

/**
 * Private component for ExposureGenericInvoiceLineItemsView only.
 * Renders the Save-and-Make-Payment trigger. Behavior depends on the DA type's
 * allowedPayableTypes config:
 *   - null / 'both' → dropdown menu lets the user pick "Pay Expenses" or "Pay Indemnity"
 *     (per-line payable_type selection happens in the table column)
 *   - 'indemnity' or 'expenses' → plain button confirms with the configured value;
 *     the per-line column is hidden by the parent and lines are auto-filled at save.
 *
 * NOT exported — only used inside ExposureGenericInvoiceLineItemsView.
 */
interface PayableTypeSelectionDropdownProps {
  disabled?: boolean;
  allowedPayableTypes: 'indemnity' | 'expenses' | 'both' | null;
  onPayableTypeSelect: (payableType: 'expenses' | 'indemnity') => void;
}

const PayableTypeSelectionDropdown: React.FC<PayableTypeSelectionDropdownProps> = ({
  disabled = false,
  allowedPayableTypes,
  onPayableTypeSelect,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  if (allowedPayableTypes === 'indemnity' || allowedPayableTypes === 'expenses') {
    return (
      <Button color="primary" disabled={disabled} onClick={() => onPayableTypeSelect(allowedPayableTypes)}>
        Save and Make Payment
      </Button>
    );
  }

  const handleMenuItemClick = (payableType: 'expenses' | 'indemnity') => {
    onPayableTypeSelect(payableType);
    setAnchorEl(null);
  };

  return (
    <>
      <Button
        color="primary"
        disabled={disabled}
        onClick={(event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget)}
        endIcon={<ArrowDropDown />}
      >
        Save and Make Payment
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem onClick={() => handleMenuItemClick('expenses')}>Pay Expenses</MenuItem>
        <MenuItem onClick={() => handleMenuItemClick('indemnity')}>Pay Indemnity</MenuItem>
      </Menu>
    </>
  );
};

const ExposureGenericInvoiceLineItemsView: React.FC<Props> = ({ exposure, open, onClose }) => {
  const { claim } = useClaim({ claimRequired: true });
  const { useFetchExposureLineItems, updateExposureLineItems } = useInvoices();
  const { currencyFormatter } = useCurrencyFormatter();
  const { classes: daScreenStyles } = useStyles();

  const { lineItems, summaryItems, settlementParams, isLoading, isError, reloadData } = useFetchExposureLineItems({
    exposureId: exposure.id,
  });
  const { daTypeConfig, invoiceConfig } = useDamageAssessmentConfig(exposure);
  const [currentFilePath, setCurrentFilePath] = useState<string | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCalculatingDepreciation, setIsCalculatingDepreciation] = useState(false);

  const [isPaymentSelectionMode, setIsPaymentSelectionMode] = useState(false);
  const [selectedPayableType, setSelectedPayableType] = useState<'expenses' | 'indemnity' | null>(null);
  const [selectedLineItems, setSelectedLineItems] = useState<Set<string>>(new Set());
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentDialogAmount, setPaymentDialogAmount] = useState(0);
  const [latestLineItemsState, setLatestLineItemsState] = useState<LineItemProps[]>([]);
  const [pendingPaymentSelectionType, setPendingPaymentSelectionType] = useState<'expenses' | 'indemnity' | null>(null);
  const [isAddInvoiceDialogOpen, setIsAddInvoiceDialogOpen] = useState(false);
  const [pendingNewInvoiceRows, setPendingNewInvoiceRows] = useState<LineItemProps[]>([]);

  const policyCurrency = getPolicyCurrency(claim);
  const isInvoiceSummaryGranularity = invoiceConfig?.entity_granularity === 'invoice';

  const transformedLineItems: LineItemProps[] = React.useMemo(() => {
    return (lineItems ?? []).map((item: LineItemWithInvoiceInfo) => ({
      id: String(item.line_item_id),
      description: item.description,
      amount: item.amount,
      covered_amount: item.covered_amount,
      coverage_decision: item.coverage_decision as unknown as UpdateExposureLineItemsRequestCoverageDecisionOption,
      payable_type:
        item.payable_type === 'expenses' || item.payable_type === 'indemnity' ? item.payable_type : undefined,
      coverage: item.coverage_key || undefined,
      exposure_id: item.exposure_id || undefined,
      explanation: item.explanation || undefined,
      document_id: item.document_id,
      invoice_id: item.invoice_id,
      amount_currency: item.amount_currency || undefined,
      item_date: item.item_date || undefined,
      invoice_date: item.invoice_date || undefined,
      conversion_date: item.conversion_date || undefined,
      conversion_rate: item.conversion_rate || undefined,
      base_amount: item.base_amount || undefined,
      category: item.category || undefined,
      months_owned: isNil(item.months_owned) ? undefined : item.months_owned,
      payment_request_id: item.payment_request_id || undefined,
      payment_financial_status: item.payment_financial_status || undefined,
      is_locked: item.is_locked || false,
      third_party_amount: item.third_party_amount ?? undefined,
      entry_type: item.entry_type || 'line_item',
    }));
  }, [lineItems]);

  // Stable Set so the dialog's claim-with-unattached-docs memo can be a real cache hit.
  const excludeDocumentIds = React.useMemo(
    () =>
      new Set(
        pendingNewInvoiceRows.map((r) => r.document_id).filter((id): id is number => id !== null && id !== undefined)
      ),
    [pendingNewInvoiceRows]
  );

  React.useEffect(() => {
    setLatestLineItemsState(transformedLineItems);
  }, [transformedLineItems]);

  // Resolve config-driven feature flags. Null config = Fast Cover defaults.
  const enableDepreciation =
    daTypeConfig?.enable_depreciation ?? exposure.damage_assessment_include_depreciation === true;
  const enableCurrencyConversion = daTypeConfig?.enable_currency_conversion ?? true;
  const enableMakePayment = daTypeConfig?.enable_make_payment ?? true;
  // null/'both' = per-line selection (column visible, dropdown menu at save time);
  // 'indemnity'/'expenses' = single-value config: column hidden, lines auto-filled
  // BTS at save time, plain Save and Make Payment button (no menu).
  const allowedPayableTypes = (daTypeConfig?.allowed_payable_types ?? null) as 'indemnity' | 'expenses' | 'both' | null;
  const forcedPayableType: 'indemnity' | 'expenses' | null =
    allowedPayableTypes === 'indemnity' || allowedPayableTypes === 'expenses' ? allowedPayableTypes : null;

  // Config-driven columns: assessment fields from daTypeConfig, invoice fields handled by wrapper.
  const columnsToShow = React.useMemo((): LineItemColumnId[] => {
    const pf = daTypeConfig?.predefined_fields;
    const cols: LineItemColumnId[] = [];

    if (isPaymentSelectionMode) cols.push('selection_checkbox');
    // Invoice fields — included here, wrapper filters by invoiceConfig
    cols.push('document', 'item_date', 'description');
    if (enableCurrencyConversion) cols.push('amount_currency');
    cols.push('amount');
    // Assessment fields — gated by daTypeConfig predefined_fields
    // Hide the per-line payable_type column when the DA config forces a single
    // value (indemnity / expenses) — lines are auto-filled at save time.
    if ((!pf || pf.payable_type?.active !== false) && forcedPayableType === null) cols.push('payable_type');
    if (!pf || pf.coverage_decision?.active !== false) cols.push('coverage_decision');
    if (enableDepreciation) {
      cols.push('category', 'depreciation', 'months_owned');
    }
    if (!pf || pf.covered_amount?.active !== false) cols.push('covered_amount');
    if (!pf || pf.explanation?.active !== false) cols.push('explanation');
    if (enableMakePayment) cols.push('financial_status');
    cols.push('actions');

    return cols;
  }, [
    isPaymentSelectionMode,
    daTypeConfig,
    enableDepreciation,
    enableCurrencyConversion,
    enableMakePayment,
    forcedPayableType,
  ]);

  // Assessment field label overrides from daTypeConfig (invoice labels handled by wrapper)
  const columnLabelOverrides = React.useMemo((): Partial<Record<string, string>> | undefined => {
    const pf = daTypeConfig?.predefined_fields;
    if (!pf) return undefined;
    return {
      ...(pf.coverage_decision?.desc && { coverage_decision: pf.coverage_decision.desc }),
      ...(pf.covered_amount?.desc && { covered_amount: pf.covered_amount.desc }),
      ...(pf.payable_type?.desc && { payable_type: pf.payable_type.desc }),
      ...(pf.explanation?.desc && { explanation: pf.explanation.desc }),
    };
  }, [daTypeConfig]);

  const currentExposureEntity = [
    {
      value: String(exposure.id),
      description: exposure.label_text || `Exposure #${exposure.claim_internal_id}`,
      damage_assessment_include_depreciation: enableDepreciation,
    },
  ];

  const handleLineItemClick = (lineItem: LineItemRow) => {
    if (lineItem.document_id) {
      setCurrentFilePath(`/api/v1/claims/${claim.id}/documents/${lineItem.document_id}`);
    }
  };

  const handleSave = React.useCallback(
    async (data: InvoiceAnalysisResponse): Promise<boolean> => {
      setIsSubmitting(true);

      try {
        // Defense-in-depth: tier-1 validateGenericInvoice already disables Save when fields
        // are missing, so this should never fire from the UI — it guards against direct/
        // race-condition callers. Tier-1 covers description/amount/date/currency/payable_type;
        // here we only re-check the new invoice_id-or-document_id contract (not in tier-1).
        const itemsMissingInvoiceAnchor = data.line_items.filter((item) => !item.invoice_id && !item.document_id);
        if (itemsMissingInvoiceAnchor.length > 0) {
          reportErrorInProductionOrThrow(
            `Cannot save line items: ${itemsMissingInvoiceAnchor.length} items are missing both invoice_id and document_id`
          );
          return false;
        }

        const lineItemsToSave = data.line_items.map((item) => ({
          id:
            typeof item.id === 'number' || (typeof item.id === 'string' && /^\d+$/.test(item.id))
              ? Number(item.id)
              : null,
          invoice_id: item.invoice_id ?? null,
          document_id: item.invoice_id ? null : (item.document_id ?? null),
          description: item.description!,
          amount: item.amount!,
          item_date: item.item_date || null,
          // When the DA config forces a single payable type, auto-fill it BTS so the
          // hidden per-line column doesn't require user input. Otherwise use the per-line value.
          payable_type: ((forcedPayableType ?? item.payable_type ?? null) ||
            null) as UpdateExposureLineItemsRequestPayableType | null,
          amount_currency: item.amount_currency || (enableCurrencyConversion ? null : policyCurrency || null),
          coverage_id: null,
          coverage_key: item.coverage || null,
          coverage_decision: item.coverage_decision as UpdateExposureLineItemsRequestCoverageDecisionOption,
          covered_amount: item.covered_amount ?? null,
          explanation: item.explanation || null,
          exposure_id: exposure.id,
          conversion_date: item.conversion_date || null,
          conversion_rate: item.conversion_rate || null,
          base_amount: item.base_amount || null,
          category: item.category || null,
          months_owned: isNil(item.months_owned) ? null : item.months_owned,
          is_depreciation_overridden: item.is_depreciation_overridden || false,
          is_locked: item.is_locked,
          payment_request_id: item.payment_request_id || null,
          third_party_amount: item.third_party_amount ?? null,
        }));

        await updateExposureLineItems(claim.id, exposure.id, lineItemsToSave);
        setPendingNewInvoiceRows([]);
        await reloadData();

        return true;
      } catch (error) {
        reportAxiosError(error);
        return false;
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      claim.id,
      exposure.id,
      updateExposureLineItems,
      reloadData,
      enableCurrencyConversion,
      policyCurrency,
      forcedPayableType,
    ]
  );

  const calculateDepreciationForItems = useCallback(
    async (itemsToCalculate: LineItemRow[], allLineItems: LineItemRow[]): Promise<LineItemRow[]> => {
      if (!claim || itemsToCalculate.length === 0) {
        return allLineItems;
      }

      try {
        setIsCalculatingDepreciation(true);

        const lineItemsData = itemsToCalculate.map((item) => ({
          id: item.id || '',
          description: item.description || '',
          category: item.category || '',
          amount: item.amount || 0,
          covered_amount: item.covered_amount || 0,
          base_amount: item.base_amount,
          invoice_currency: item.amount_currency || policyCurrency || 'USD',
          invoice_date: item.item_date,
        }));

        const response = await axios.post(
          `/api/v1/claims/${claim.id}/invoice_based_da/exposure/${exposure.id}/calculate_depreciation`,
          {
            line_items: lineItemsData,
          }
        );

        const results = response.data.results || [];
        return allLineItems.map((lineItem) => {
          const result = results.find((r: { id: string }) => r.id === lineItem.id);
          if (result) {
            return {
              ...lineItem,
              months_owned: result.months_owned,
              covered_amount: result.settlement_amount,
              is_depreciation_overridden: false,
            };
          }
          return lineItem;
        });
      } catch (error) {
        reportAxiosError(error);
        return allLineItems;
      } finally {
        setIsCalculatingDepreciation(false);
      }
    },
    [claim, exposure.id, policyCurrency]
  );

  const handleDepreciationClick = useCallback(
    async (lineItem: LineItemRow): Promise<LineItemRow | undefined> => {
      if (!lineItem.item_date) {
        reportAxiosError(new Error('Line item must have a date to calculate depreciation'));
        return undefined;
      }

      const { isEligible } = isDepreciationEligible(lineItem, policyCurrency);
      if (!isEligible) {
        return undefined;
      }

      const updatedItems = await calculateDepreciationForItems([lineItem], [lineItem]);
      return updatedItems[0];
    },
    [calculateDepreciationForItems, policyCurrency]
  );

  const handleCalculateAllDepreciations = useCallback(
    async (currentLineItems: LineItemRow[]): Promise<LineItemRow[]> => {
      const eligibleItems = currentLineItems.filter((row) => isDepreciationEligible(row, policyCurrency).isEligible);

      if (eligibleItems.length === 0) {
        return currentLineItems;
      }

      const itemsWithoutDate = eligibleItems.filter((item) => !item.item_date);
      if (itemsWithoutDate.length > 0) {
        reportAxiosError(new Error('All line items must have a date to calculate depreciation'));
        return currentLineItems;
      }

      return await calculateDepreciationForItems(eligibleItems, currentLineItems);
    },
    [calculateDepreciationForItems, policyCurrency]
  );

  const enterPaymentSelectionMode = useCallback(
    (payableType: 'expenses' | 'indemnity') => {
      setSelectedPayableType(payableType);
      setIsPaymentSelectionMode(true);

      const relevantItemIds = latestLineItemsState
        .filter((item) => {
          // Keep refund rows (negative covered_amount) so the pre-selected total matches the settlement net.
          if (item.payable_type !== payableType || !item.id || (item.covered_amount || 0) === 0) {
            return false;
          }

          if (item.is_locked) {
            return false;
          }

          // Exclude items with active payment status
          if (item.payment_financial_status) {
            const statusInfo =
              FINANCIAL_STATUS_DICT[item.payment_financial_status as keyof typeof FINANCIAL_STATUS_DICT];
            if (statusInfo && statusInfo.is_active) {
              return false;
            }
          }

          return true;
        })
        .map((item) => item.id!);

      setSelectedLineItems(new Set(relevantItemIds));
    },
    [latestLineItemsState]
  );

  // Orchestrate entering payment selection mode after data refresh
  React.useEffect(() => {
    if (pendingPaymentSelectionType && transformedLineItems.length > 0 && !isLoading) {
      // Data has been refreshed, now we can safely enter payment selection mode with fresh state
      enterPaymentSelectionMode(pendingPaymentSelectionType);
      setPendingPaymentSelectionType(null);
    }
  }, [pendingPaymentSelectionType, transformedLineItems, isLoading, enterPaymentSelectionMode]);

  const handlePayableTypeSelect = useCallback(
    async (payableType: 'expenses' | 'indemnity', currentData: InvoiceAnalysisResponse) => {
      const success = await handleSave(currentData);

      if (success) {
        // Set intent to enter payment selection mode - useEffect will handle it after data refresh
        setPendingPaymentSelectionType(payableType);
      }
    },
    [handleSave]
  );

  const handleSelectionChange = useCallback(
    (itemId: string, selected: boolean) => {
      let newSet: Set<string>;
      setSelectedLineItems((prev) => {
        newSet = new Set(prev);
        if (selected) {
          newSet.add(itemId);
        } else {
          newSet.delete(itemId);
        }
        return newSet;
      });

      if (showPaymentDialog) {
        const updatedAmount = latestLineItemsState
          .filter((item) => item.id && newSet!.has(item.id))
          .reduce((sum, item) => sum.plus(toCentsDecimal(item.covered_amount)), DECIMAL_ZERO);
        setPaymentDialogAmount(updatedAmount.toNumber());
      }
    },
    [showPaymentDialog, latestLineItemsState]
  );

  const handleCancelPaymentSelection = useCallback(() => {
    setIsPaymentSelectionMode(false);
    setSelectedPayableType(null);
    setSelectedLineItems(new Set());
  }, []);

  const calculateSelectedAmount = useCallback((): number => {
    const coveredSum = latestLineItemsState
      .filter((item) => item.id && selectedLineItems.has(item.id))
      .reduce((sum, item) => sum.plus(toCentsDecimal(item.covered_amount)), DECIMAL_ZERO);

    // Apply co-insurance rate to get eligible amount (deductible applied in payment dialog)
    const rate = settlementParams?.coinsurance_insurer_percentage ?? 1.0;
    return multiplyToCentsDecimal(coveredSum, rate).toNumber();
  }, [latestLineItemsState, selectedLineItems, settlementParams]);

  const selectedLineItemIds = React.useMemo(() => {
    return Array.from(selectedLineItems)
      .map((id) => {
        const item = latestLineItemsState.find((item) => item.id === id);
        return item?.id ? Number(item.id) : undefined;
      })
      .filter((id): id is number => id !== undefined);
  }, [selectedLineItems, latestLineItemsState]);

  const handleCreatePayment = useCallback(() => {
    const currentAmount = calculateSelectedAmount();
    setPaymentDialogAmount(currentAmount);
    setShowPaymentDialog(true);
  }, [calculateSelectedAmount]);

  const handlePaymentDialogClose = useCallback(() => {
    setShowPaymentDialog(false);
    handleCancelPaymentSelection();
  }, [handleCancelPaymentSelection]);

  const handlePaymentSuccess = useCallback(async () => {
    await reloadData();
    setShowPaymentDialog(false);
    handleCancelPaymentSelection();
  }, [handleCancelPaymentSelection, reloadData]);

  // Use function form of customButtons to get current data from InvoiceRecommendationAnalysis
  const customButtons = useCallback(
    (
      getCurrentData: () => InvoiceAnalysisResponse,
      helpers: {
        handleCalculateAllDepreciations?: () => Promise<void>;
        isCalculatingDepreciation?: boolean;
        shouldShowDepreciationButton?: boolean;
      }
    ) => {
      if (isPaymentSelectionMode) {
        return (
          <div className="inline-flex gap-8">
            <Button variant="outlined" onClick={handleCancelPaymentSelection}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCreatePayment}
              disabled={selectedLineItems.size === 0}
            >
              Create{' '}
              {selectedPayableType ? selectedPayableType.charAt(0).toUpperCase() + selectedPayableType.slice(1) : ''}{' '}
              Payment ({selectedLineItems.size} items -{' '}
              {currencyFormatter.format(calculateSelectedAmount(), policyCurrency)})
            </Button>
          </div>
        );
      }

      return (
        <div className="inline-flex gap-8">
          {isInvoiceSummaryGranularity && (
            <Button
              color="primary"
              size="small"
              disabled={isSubmitting}
              onClick={() => setIsAddInvoiceDialogOpen(true)}
            >
              <AddIcon className={daScreenStyles.leftButtonIcon} /> Add invoice
            </Button>
          )}
          {helpers.shouldShowDepreciationButton && helpers.handleCalculateAllDepreciations && (
            <Button
              color="primary"
              disabled={isSubmitting || helpers.isCalculatingDepreciation}
              onClick={helpers.handleCalculateAllDepreciations}
            >
              {helpers.isCalculatingDepreciation ? 'Calculating...' : 'Calculate All Depreciations'}
            </Button>
          )}
          {enableMakePayment && (
            <PayableTypeSelectionDropdown
              disabled={isSubmitting}
              allowedPayableTypes={allowedPayableTypes}
              onPayableTypeSelect={(payableType) => handlePayableTypeSelect(payableType, getCurrentData())}
            />
          )}
        </div>
      );
    },
    [
      isPaymentSelectionMode,
      handleCancelPaymentSelection,
      handleCreatePayment,
      selectedLineItems.size,
      selectedPayableType,
      currencyFormatter,
      calculateSelectedAmount,
      policyCurrency,
      isSubmitting,
      handlePayableTypeSelect,
      enableMakePayment,
      isInvoiceSummaryGranularity,
      daScreenStyles.leftButtonIcon,
      allowedPayableTypes,
    ]
  );

  const validateGenericInvoice = useCallback(
    (lineItems: LineItemRow[]): string | undefined => {
      if (lineItems.some((item) => !item.item_date)) {
        return 'Please provide dates for all line items';
      }

      if (enableCurrencyConversion && lineItems.some((item) => !item.amount_currency)) {
        return 'Please provide currency for all line items';
      }

      if (lineItems.some((item) => !item.description || item.description.trim() === '')) {
        return 'Please provide descriptions for all line items';
      }

      if (lineItems.some((item) => isNil(item.amount))) {
        return 'Please provide amounts for all line items';
      }

      // Per-line check applies when the column is visible (null/'both' configs).
      // For forced single-value configs the column is hidden and lines are auto-filled.
      if (forcedPayableType === null && lineItems.some((item) => !item.payable_type)) {
        return 'Please select payable type for all line items';
      }

      return undefined;
    },
    [enableCurrencyConversion, forcedPayableType]
  );

  const mainTitle =
    isPaymentSelectionMode && selectedPayableType
      ? `Exposure ${exposure.label_text || `#${exposure.claim_internal_id}`} - Select ${selectedPayableType.charAt(0).toUpperCase() + selectedPayableType.slice(1)} Items for Payment`
      : `Exposure ${exposure.label_text || `#${exposure.claim_internal_id}`}`;

  const deductibleCanBeApplied: boolean = claim.deductible_flow_type === 'explicit' && !wasDeductibleApplied(exposure);
  // Only the GC flow pre-applies the deductible at payment-creation time. The explicit Auto
  // flow (Auto + AUTO_USE_GC_DEDUCTIBLE_FLOW on) inherits GC's apply-deductible affordance
  // but NOT its pre-fill — Auto adjusters opt in via ApplyDeductibleCheckbox, matching
  // MakePaymentRequestDialog's contract.
  const shouldPreFillDeductibleOnPayment = claim.type === 'general_claim' && deductibleCanBeApplied;
  const exposureDeductible = exposure.deductible_amount || 0;

  return (
    <>
      <ConfiguredInvoiceRecommendationAnalysis
        invoiceConfig={invoiceConfig}
        open={open}
        onClose={onClose}
        lineItems={transformedLineItems}
        extraPendingRows={pendingNewInvoiceRows}
        isLoading={isLoading}
        isError={isError}
        entities={currentExposureEntity}
        disabled={isPaymentSelectionMode}
        submitting={isSubmitting}
        coInsuranceCoverageRates={{}}
        lineItemColumnsToShow={columnsToShow}
        shouldShowCoverageSummary={false}
        shouldShowInvoiceSummary={false}
        customMainTitle={mainTitle}
        shouldShowLineItemsAmountSummary={true}
        summaryItems={summaryItems}
        settlementParams={settlementParams}
        columnLabelOverrides={columnLabelOverrides}
        onSave={handleSave}
        validateBeforeSave={validateGenericInvoice}
        possibleCurrencies={CURRENCY_OPTIONS}
        filePath={currentFilePath}
        onCloseFilePath={() => setCurrentFilePath(undefined)}
        onLineItemClick={handleLineItemClick}
        policyCurrency={policyCurrency}
        claim={claim}
        shouldAutoHandleMultiCurrencyColumns={true}
        shouldShowDatePerLineItem={true}
        onDepreciationClick={enableDepreciation ? handleDepreciationClick : undefined}
        onCalculateAllDepreciations={enableDepreciation ? handleCalculateAllDepreciations : undefined}
        isCalculatingDepreciation={isCalculatingDepreciation}
        isPaymentSelectionMode={isPaymentSelectionMode}
        selectedPayableType={selectedPayableType}
        selectedLineItems={selectedLineItems}
        onSelectionChange={handleSelectionChange}
        customButtons={customButtons}
        shouldReloadItemsAfterInit={true}
      />
      {showPaymentDialog && selectedPayableType && (
        <PaymentRequestContainer
          cardDialogProps={{
            isDialog: true,
            open: showPaymentDialog,
            onClose: handlePaymentDialogClose,
            maxWidth: 'md',
            fullWidth: true,
          }}
          onClose={handlePaymentDialogClose}
          onUpdate={handlePaymentSuccess}
          claim={claim}
          exposure={exposure}
          payableWithReserve={selectedPayableType === 'expenses' ? exposure.expenses : exposure.indemnity}
          payableType={selectedPayableType!}
          defaultAmount={
            shouldPreFillDeductibleOnPayment
              ? Math.max(paymentDialogAmount - exposureDeductible, 0)
              : paymentDialogAmount
          }
          overrideInitialValues={
            shouldPreFillDeductibleOnPayment
              ? {
                  deductible_considered_amount: exposureDeductible,
                }
              : {}
          }
          additionalPayload={{
            invoice_line_item_ids: selectedLineItemIds,
          }}
          forceManualDataInit
        />
      )}
      <AddInvoiceForExposureDialog
        open={isAddInvoiceDialogOpen}
        onClose={() => setIsAddInvoiceDialogOpen(false)}
        excludeDocumentIds={excludeDocumentIds}
        onSelect={(document) => {
          setPendingNewInvoiceRows((prev) => [
            ...prev,
            {
              id: `pending-invoice-${document.id}-${Date.now()}`,
              description: '',
              amount: undefined,
              covered_amount: undefined,
              coverage_decision: 'covered' as unknown as UpdateExposureLineItemsRequestCoverageDecisionOption,
              document_id: document.id,
              invoice_id: undefined,
              entry_type: 'invoice_summary',
              is_locked: false,
            } as LineItemProps,
          ]);
          setIsAddInvoiceDialogOpen(false);
        }}
      />
    </>
  );
};

export default ExposureGenericInvoiceLineItemsView;
